import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, clearLegacyToken, getLegacyToken, readCookie } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { identifyMember, resetMember } from "@/lib/heycatch";

/**
 * Authentication state.
 *
 * Session transport (see src/lib/api.js and backend/core.py):
 *  - The session JWT lives in an HttpOnly, Secure cookie issued by the API. It
 *    is never readable by JavaScript and is never written to localStorage.
 *  - `sm_signed_in` is a NON-SENSITIVE hint flag (value "1") that only tells the
 *    app whether it is worth calling /auth/me on boot; it grants no access on
 *    its own and the server remains the only authority.
 *  - LEGACY: sessions created before the migration still have a bearer token in
 *    localStorage. On the first successful /auth/me we exchange it for cookies
 *    via POST /auth/session and then delete it, so nobody gets logged out.
 */
const AuthContext = createContext(null);

const SIGNED_IN_HINT = "sm_signed_in";
const SESSION_COOKIE_HINT = "sm_csrf"; // readable companion of the HttpOnly session cookie

function setSignedInHint(value) {
  try {
    if (value) localStorage.setItem(SIGNED_IN_HINT, "1");
    else localStorage.removeItem(SIGNED_IN_HINT);
  } catch (err) {
    devWarn("AuthContext.hintStorage", err);
  }
}

function hasSignedInHint() {
  try {
    return localStorage.getItem(SIGNED_IN_HINT) === "1";
  } catch (err) {
    devWarn("AuthContext.hintStorage", err);
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [sponsorProfile, setSponsorProfile] = useState(null);
  const [unreadNotifications, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState({ open: false, message: "" });

  /** Move a legacy localStorage session onto cookies, once. */
  const upgradeLegacySession = useCallback(async () => {
    if (!getLegacyToken()) return;
    try {
      await api.post("/auth/session");
      clearLegacyToken();
    } catch (err) {
      // Keep the legacy token if the exchange fails - the member stays signed in
      // with Bearer auth and we simply retry on the next load.
      devWarn("AuthContext.upgradeSession", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    const maybeSignedIn =
      hasSignedInHint() || !!getLegacyToken() || !!readCookie(SESSION_COOKIE_HINT);
    if (!maybeSignedIn) {
      setUser(null);
      setSponsorProfile(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setSponsorProfile(data.sponsor_profile);
      setUnread(data.unread_notifications || 0);
      setSignedInHint(true);
      // Attach analytics to the member. Sends only the internal user id and a
      // coarse billing tier - see src/lib/heycatch.js for the privacy contract.
      // Repeat refreshes with an unchanged identity are ignored there.
      identifyMember(data.user);
      await upgradeLegacySession();
      return data.user;
    } catch (err) {
      // 401/403 means the session is gone: clear local hints and sign out.
      devWarn("AuthContext.refresh", err);
      clearLegacyToken();
      setSignedInHint(false);
      setUser(null);
      setSponsorProfile(null);
      // The session is over, so the analytics identity must not linger on this
      // device for whoever browses next.
      resetMember();
      return null;
    } finally {
      setLoading(false);
    }
  }, [upgradeLegacySession]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email, password) => {
      // The API sets the HttpOnly session cookie on this response; the token in
      // the body is intentionally ignored and never stored.
      const { data } = await api.post("/auth/login", { email, password });
      setSignedInHint(true);
      clearLegacyToken();
      setUser(data.user);
      await refresh();
      return data.user;
    },
    [refresh]
  );

  const register = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/register", payload);
      setSignedInHint(true);
      clearLegacyToken();
      setUser(data.user);
      await refresh();
      return data.user;
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    // Clear local state immediately so the UI never looks signed in, then ask
    // the server to expire the cookie.
    setSignedInHint(false);
    clearLegacyToken();
    setUser(null);
    setSponsorProfile(null);
    setUnread(0);
    // Return the visitor to anonymous so the next person on this device is not
    // attributed to the member who just signed out.
    resetMember();
    try {
      await api.post("/auth/logout");
    } catch (err) {
      devWarn("AuthContext.logout", err);
    }
  }, []);

  const openPaywall = useCallback((message) => setPaywall({ open: true, message: message || "" }), []);
  const closePaywall = useCallback(() => setPaywall({ open: false, message: "" }), []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      sponsorProfile,
      setSponsorProfile,
      unreadNotifications,
      setUnread,
      loading,
      login,
      register,
      logout,
      refresh,
      paywall,
      openPaywall,
      closePaywall,
    }),
    [user, sponsorProfile, unreadNotifications, loading, login, register, logout, refresh, paywall, openPaywall, closePaywall]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
